/**
 * Step for creating a blank DTA - collects vendor and domain.
 */

import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import FormField from "@cloudscape-design/components/form-field"
import Input from "@cloudscape-design/components/input"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useMemo, useState } from "react"

import type { Vendor } from "../../services/vendorRegistryApi"

interface BlankDtaStepProps {
  vendors: Vendor[]
  vendorLoadError: string | null
  onSubmit: (vendorId: string, vendorName: string, domain: string) => void
  onBack: () => void
  creating: boolean
  /**
   * Error from a failed create attempt (e.g. the study has no approved protocol). Rendered inline
   * near the action so the failure is never silently swallowed.
   */
  createError: string | null
}

export default function BlankDtaStep({
  vendors,
  vendorLoadError,
  onSubmit,
  onBack,
  creating,
  createError,
}: BlankDtaStepProps) {
  const [vendorId, setVendorId] = useState("")
  const [domain, setDomain] = useState("")

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.vendor_id, label: v.vendor_name })),
    [vendors],
  )
  const selectedVendor = vendors.find((v) => v.vendor_id === vendorId)
  const canSubmit = vendorId && domain && !creating

  return (
    <SpaceBetween size="l">
      <Box variant="p" color="text-body-secondary">
        Select a vendor and domain to create an empty Data Transfer Agreement.
      </Box>

      <FormField label="Vendor">
        {vendorLoadError ? (
          <Alert type="error">{vendorLoadError}</Alert>
        ) : (
          <Select
            selectedOption={
              selectedVendor
                ? { value: selectedVendor.vendor_id, label: selectedVendor.vendor_name }
                : null
            }
            onChange={({ detail }) => setVendorId(detail.selectedOption.value ?? "")}
            placeholder="Select vendor..."
            options={vendorOptions}
          />
        )}
      </FormField>

      <FormField label="Domain (SDTM code)">
        <Input
          value={domain}
          onChange={({ detail }) => setDomain(detail.value.toUpperCase())}
          placeholder="e.g. DM, LB, AE"
        />
      </FormField>

      {createError && <Alert type="error">{createError}</Alert>}

      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={onBack}>
            Back
          </Button>
          <Button
            variant="primary"
            loading={creating}
            disabled={!canSubmit}
            onClick={() =>
              canSubmit && onSubmit(vendorId, selectedVendor?.vendor_name || "", domain)
            }
          >
            {creating ? "Creating..." : "Create DTA"}
          </Button>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  )
}
